import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { StatCounter } from "@/components/StatCounter";
import { VideoFeature } from "@/components/VideoFeature";
import { HighlightsStrip } from "@/components/HighlightsStrip";
import { CampReel } from "@/components/CampReel";
import { DonateButton } from "@/components/DonateButton";
import { DonationImpactBadge } from "@/components/DonationImpactBadge";
import { IMAGES, STATS, CLUBS, TESTIMONIALS, TIMELINE } from "@/data/content";
import { GrowthChart } from "@/components/GrowthChart";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const FloatSpark = ({ className = "", color = "#D95D39", delay = "0s", size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    aria-hidden="true"
    className={`animate-float-slow absolute ${className}`}
    style={{ animationDelay: delay }}
  >
    <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" />
  </svg>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const chipY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-sand grain" data-testid="hero-section">
      <div className="absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-teal-subtle blur-[90px]" />
      <div className="absolute -left-40 bottom-10 h-[28rem] w-[28rem] rounded-full bg-ember-subtle blur-[90px]" />
      <FloatSpark className="left-[6%] top-28 hidden md:block" color="#0D7C85" size={26} />
      <FloatSpark className="right-[10%] top-40 hidden md:block" color="#E9A03A" delay="1.2s" size={18} />
      <FloatSpark className="left-[42%] bottom-44 hidden lg:block" color="#D95D39" delay="2s" size={20} />
      <FloatSpark className="left-[28%] top-20 hidden lg:block" color="#E9A03A" delay="0.6s" size={14} />
      <FloatSpark className="right-[38%] bottom-24 hidden md:block" color="#0D7C85" delay="1.7s" size={16} />
      <div className="absolute left-[16%] bottom-32 hidden h-4 w-4 animate-float-slow rounded-full bg-ember lg:block" style={{ animationDelay: "0.9s" }} />
      <div className="absolute right-[22%] top-24 hidden h-3 w-3 animate-float-slow rounded-full bg-teal md:block" style={{ animationDelay: "1.5s" }} />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 pb-24 pt-16 sm:pt-24 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div>
          <h1 className="font-display font-extrabold tracking-tight text-coal text-6xl sm:text-7xl lg:text-8xl leading-[1.02]">
            <MaskedLine delay={0.25}>Language</MaskedLine>
            <MaskedLine delay={0.4}>
              is{" "}
              <span className="relative inline-block text-ember">
                power.
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 14" fill="none" aria-hidden="true">
                  <path d="M3 10 Q 28 3 53 10 T 103 10 T 153 10 T 198 10" stroke="#E9A03A" strokeWidth="6" strokeLinecap="round" className="squiggle-draw" />
                </svg>
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-coal-muted"
            data-testid="hero-subline"
          >
            The EduPower Foundation bridges the gap between potential and opportunity, through
            reading, writing, debating and filmmaking clubs, Grade 12 study camps, and the educators
            who show up every day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/english-is-lekka"
              data-testid="hero-explore-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-teal px-9 py-4 font-heading font-bold text-base text-sand transition-colors duration-300 hover:bg-teal-light"
            >
              Explore our programmes
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <DonateButton size="lg" testId="hero-donate-button">Sponsor a learner</DonateButton>
          </motion.div>

          <DonationImpactBadge className="mt-6" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="overflow-hidden rounded-[2.5rem] rounded-tr-[6rem] border border-coal/10 shadow-[0_30px_80px_-30px_rgba(13,124,133,0.5)]">
              <img
                src={IMAGES.hero}
                alt="Learners in a Cape Town classroom"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            <motion.img
              src="/logo.png"
              alt="EduPower Foundation artwork, a child reaching for the stars across South Africa"
              initial={{ opacity: 0, rotate: -18, scale: 0.6 }}
              animate={{ opacity: 1, rotate: -8, scale: 1 }}
              transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="animate-float-slow absolute -left-16 -top-12 w-44 rounded-[2rem] shadow-[0_24px_60px_-18px_rgba(233,160,58,0.65)] xl:w-52"
              style={{ animationDelay: "1.4s" }}
              data-testid="hero-logo-artwork"
            />
            <motion.div
              style={{ y: chipY }}
              className="animate-float-slow absolute -left-10 bottom-14 rounded-2xl bg-ember px-6 py-4 shadow-[0_20px_50px_-15px_rgba(217,93,57,0.7)]"
              data-testid="hero-growth-chip"
            >
              <span className="font-display text-3xl font-extrabold text-sand">500</span>
              <span className="block text-xs font-semibold text-sand/85">learners by 2026, help us get there</span>
            </motion.div>
            <div className="absolute -right-6 top-10 rounded-2xl border border-coal/10 bg-white/90 px-5 py-3 shadow-lg backdrop-blur">
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-ember font-bold">
                English is Lekka
              </span>
              <span className="block font-heading text-sm font-bold text-coal">4 clubs. One movement.</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const StatsBand = () => (
  <section className="relative border-y border-coal/10 bg-white" data-testid="hero-stats-band">
    <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-coal/10 px-6 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <div key={s.label} className="px-4 py-7 text-center">
          <StatCounter
            value={s.value}
            prefix={s.prefix || ""}
            suffix={s.suffix}
            testId={s.testId}
            className={`font-display text-3xl font-extrabold sm:text-4xl ${i % 2 ? "text-ember" : "text-teal"}`}
          />
          <p className="mt-1.5 text-xs leading-snug text-coal-muted">{s.label}</p>
        </div>
      ))}
    </div>
  </section>
);

const Programmes = () => (
  <section className="py-16 sm:py-20" data-testid="programmes-section">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal>
        <Eyebrow>What we do</Eyebrow>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
          Three programmes. One goal: <span className="text-teal">opportunity.</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-5 lg:grid-cols-3">
        <Reveal className="lg:row-span-2">
          <Link
            to="/english-is-lekka"
            data-testid="programme-card-lekka"
            className="group relative flex h-full min-h-[26rem] flex-col justify-end overflow-hidden rounded-[2rem] bg-coal-dark p-8"
          >
            <img
              src={IMAGES.debate}
              alt="English is Lekka learners debating"
              className="absolute inset-0 h-full w-full object-cover opacity-50 transition-all duration-700 group-hover:scale-105 group-hover:opacity-60"
            />
            <div className="relative">
              <span className="font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-ember-light">
                Flagship programme
              </span>
              <h3 className="mt-2 font-display text-3xl font-extrabold text-sand">English is Lekka</h3>
              <p className="mt-3 text-sm leading-relaxed text-sand/80">
                A transformative literacy and social justice programme introducing learners to arts
                and media careers through reading, debating, writing and filmmaking, with annual
                celebrations and notable speakers.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {Object.values(CLUBS).map((c) => (
                  <span
                    key={c.slug}
                    className="rounded-full border border-sand/30 bg-coal-dark/40 px-4 py-1.5 text-sm font-semibold text-sand"
                  >
                    {c.name}
                  </span>
                ))}
              </div>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-heading font-bold text-ember-light">
                Discover the clubs
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </Link>
        </Reveal>

        <Reveal delay={0.1}>
          <Link
            to="/edupower13"
            data-testid="programme-card-edupower13"
            className="group flex h-full flex-col justify-between rounded-[2rem] bg-teal p-8 transition-colors duration-500 hover:bg-teal-light"
          >
            <div>
              <span className="font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-sand/70">
                Academic mentorship
              </span>
              <h3 className="mt-2 font-display text-3xl font-extrabold text-sand">EduPower13</h3>
              <p className="mt-3 text-sm leading-relaxed text-sand/85">
                13 schools in the Metro South district taken under the wing of the English
                department, teachers mentoring learners where they struggle most.
              </p>
            </div>
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-heading font-bold text-sand">
              Learn more
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </Link>
        </Reveal>

        <Reveal delay={0.2}>
          <Link
            to="/wellbeing"
            data-testid="programme-card-wellbeing"
            className="group flex h-full flex-col justify-between rounded-[2rem] bg-ember p-8 transition-colors duration-500 hover:bg-ember-light"
          >
            <div>
              <span className="font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-sand/80">
                Your Story Matters
              </span>
              <h3 className="mt-2 font-display text-3xl font-extrabold text-sand">Wellbeing</h3>
              <p className="mt-3 text-sm leading-relaxed text-sand/90">
                Safe, supportive spaces where young people share experiences, find their voice and
                build resilience, for learners and the teachers beside them.
              </p>
            </div>
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-heading font-bold text-sand">
              Learn more
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Impact = () => (
  <section className="relative overflow-hidden bg-teal-subtle py-16" data-testid="impact-section">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2">
      <Reveal>
        <Eyebrow>Why sponsors back us</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
          Transformative impact at <span className="text-ember">R4,427</span> per learner, per year.
        </h2>
        <p className="mt-5 max-w-lg text-base leading-relaxed text-coal-muted">
          At scale, English is Lekka delivers multi-dimensional impact, academic rigour, cultural
          exposure and confidence, at a cost designed to shrink as we grow. As reach expands to 500
          learners, cost efficiency improves without compromising quality.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <DonateButton testId="impact-donate-button">Become a backer</DonateButton>
          <Link
            to="/about"
            data-testid="impact-story-link"
            className="inline-flex items-center gap-2 rounded-full border border-coal/15 px-6 py-2.5 font-heading text-sm font-bold text-coal transition-colors hover:border-teal hover:text-teal"
          >
            Read our story
            <ArrowRight size={15} />
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="grid grid-cols-2 gap-4">
          {[
            { v: "2", l: "annual residential camps from 2026" },
            { v: "100", l: "learners per camp, fully supported" },
            { v: "500", l: "learners at full scale" },
            { v: "1,131+", l: "teacher training seats since 2022" },
          ].map((s, i) => (
            <div
              key={s.l}
              data-testid={`impact-stat-${i}`}
              className={`rounded-3xl p-6 ${i % 2 ? "bg-teal text-sand" : "bg-white text-coal"} shadow-[0_10px_40px_-20px_rgba(13,124,133,0.4)]`}
            >
              <span className={`font-display text-4xl font-extrabold ${i % 2 ? "text-sand" : "text-teal"}`}>
                {s.v}
              </span>
              <p className={`mt-2 text-xs leading-snug ${i % 2 ? "text-sand/75" : "text-coal-muted"}`}>{s.l}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const TestimonialCard = ({ t, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * 0.08} className="h-full">
      <figure
        className="flex h-full flex-col rounded-3xl bg-white p-6 shadow-[0_14px_44px_-26px_rgba(26,29,36,0.35)]"
        data-testid={`testimonial-card-${i}`}
      >
        <Quote size={26} className="text-ember" />
        <blockquote
          className={`mt-4 text-sm leading-relaxed text-coal-muted ${open ? "" : "line-clamp-5"}`}
          data-testid={i === 0 ? "testimonial-text" : undefined}
        >
          {t.text}
        </blockquote>
        <button
          data-testid={`testimonial-toggle-${i}`}
          onClick={() => setOpen((v) => !v)}
          className="mt-2 self-start font-heading text-xs font-bold text-teal transition-colors hover:text-ember"
        >
          {open ? "Show less" : "Read more"}
        </button>
        <figcaption className="mt-4 border-t border-coal/5 pt-4">
          <p className="font-heading text-sm font-extrabold text-teal" data-testid={i === 0 ? "testimonial-name" : undefined}>
            {t.name}
          </p>
          <p className="text-xs text-coal-muted">{t.school}</p>
          {t.badge && (
            <p className="mt-2 inline-block rounded-full bg-ember-subtle px-3 py-1 font-mono-brand text-[10px] font-bold uppercase tracking-wider text-ember">
              {t.badge}
            </p>
          )}
        </figcaption>
      </figure>
    </Reveal>
  );
};

const Testimonials = () => (
  <section className="py-14 sm:py-16" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal className="text-center">
        <Eyebrow>Voices of the programme</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
          Learners say it best
        </h2>
      </Reveal>
      <div className="mt-12 grid items-stretch gap-5 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <TestimonialCard key={t.name} t={t} i={i} />
        ))}
      </div>
    </div>
  </section>
);

const TimelineTeaser = () => (
  <section className="bg-teal grain py-16" data-testid="timeline-teaser">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="font-mono-brand uppercase tracking-[0.25em] text-xs font-bold inline-flex items-center gap-2 text-sand">
            <span className="w-8 h-px bg-sand" />
            Since 2022
          </span>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-sand sm:text-5xl">
            Four years. One rising tide.
          </h2>
        </div>
        <Link
          to="/about"
          data-testid="timeline-teaser-link"
          className="group inline-flex items-center gap-2 rounded-full border border-sand/40 px-6 py-2.5 font-heading text-sm font-bold text-sand transition-colors hover:bg-sand hover:text-teal"
        >
          Full timeline
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {TIMELINE.map((t, i) => (
          <Reveal key={t.year} delay={i * 0.08}>
            <Link
              to="/about"
              className="group flex h-full flex-col rounded-3xl bg-sand p-6 shadow-[0_14px_40px_-20px_rgba(8,78,84,0.5)] transition-all duration-500 hover:-translate-y-1.5 hover:bg-white"
              data-testid={`timeline-teaser-${t.year}`}
            >
              <span className={`font-display text-3xl font-extrabold ${i % 2 ? "text-teal" : "text-ember"}`}>
                {t.year}
              </span>
              <h3 className="mt-3 font-heading text-sm font-bold text-coal">{t.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-coal-muted line-clamp-4">{t.text}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Marquee />
      <Hero />
      <StatsBand />
      <VideoFeature />
      <HighlightsStrip />
      <CampReel />
      <Programmes />
      <Impact />
      <GrowthChart />
      <Testimonials />
      <TimelineTeaser />
    </>
  );
}
